import { useState, useEffect } from "react";

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);

    setMatches(media.matches);

    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    media.addEventListener("change", listener);

    return () => {
      media.removeEventListener("change", listener);
    };
  }, [query]);

  return matches;
};

export const useMediaQueryV2 = (
  query: string,
  initialValue: boolean,
): boolean => {
  const [matches, setMatches] = useState(initialValue);

  useEffect(() => {
    const media = window.matchMedia(query);

    setMatches(media.matches);

    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    media.addEventListener("change", listener);

    return () => {
      media.removeEventListener("change", listener);
    };
  }, [query]);

  return matches;
};

type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl";
type BreakpointMap = {
  [key in Breakpoint]: boolean;
};

const breakpoints: Record<Breakpoint, number> = {
  xs: 576,
  sm: 768,
  md: 992,
  lg: 1200,
  xl: 1400,
};

export const useBreakpoint = (): BreakpointMap => {
  const [current, setCurrent] = useState<BreakpointMap>({
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: false,
  });

  const evaluate = () => {
    const width = window.innerWidth;
    const result: BreakpointMap = {
      xs: width < breakpoints.xs,
      sm: width >= breakpoints.xs && width < breakpoints.md,
      md: width >= breakpoints.md && width < breakpoints.lg,
      lg: width >= breakpoints.lg && width < breakpoints.xl,
      xl: width >= breakpoints.lg,
    };
    setCurrent(result);
  };

  useEffect(() => {
    evaluate();
    window.addEventListener("resize", evaluate);
    return () => window.removeEventListener("resize", evaluate);
  }, []);

  return current;
};
